import { Pill, type PillColor } from '@mes-amis/galette-design-system';
import { documentStatusConfig } from '@mes-amis/super-form';
import type { Document } from '@/db';
import { describeDocumentSyncError } from '@/lib/sync-errors';

/**
 * Go-only sync states the shared status config doesn't know about; everything
 * else (completed/Signed, Needs Signature, Re-opened, In-Progress default)
 * reuses documentStatusConfig so every list matches the editor badge.
 */
const GO_STATUS_CONFIG: Record<string, { label: string; color: PillColor }> = {
  draft: { label: 'Draft', color: 'gray' },
  sync_error: { label: 'Sync Error', color: 'red' },
  deleted_online: { label: 'Deleted Online', color: 'gray' },
};

/** Derived display status: a sync error/conflict overrides the stale underlying
 *  status (a deleted_online row still carries e.g. 'pending', which would read
 *  "In-Progress"). */
export function getDisplayStatus(doc: Pick<Document, 'status' | 'sync_error_type'>): string {
  if (doc.sync_error_type === 'deleted_online') return 'deleted_online';
  if (doc.sync_error_type === 'permission') return 'permission_sync_error';
  if (doc.sync_error_type === 'other') return 'sync_error';
  return doc.status;
}

/**
 * The status chip for a GO document row — shared by the client Documents tab
 * and the dashboard Recent Docs list so a document never shows different
 * states in the two places. `hasSignatureFlow` makes completed-with-signatures
 * documents read "Signed".
 */
export default function DocumentStatusPill({
  document,
  hasSignatureFlow = false,
}: {
  document: Pick<Document, 'status' | 'sync_error_type' | 'sync_error_message'>;
  hasSignatureFlow?: boolean;
}) {
  const status = getDisplayStatus(document);
  const { label, color } = GO_STATUS_CONFIG[status] ?? documentStatusConfig(status, hasSignatureFlow);
  // A bare "Sync Error" tells the user nothing they can act on. The full
  // explanation and the retry live in Settings > Sync; the tooltip is here so
  // the list itself at least names the problem.
  const tooltip = status === 'sync_error' && document.sync_error_message
    ? `${describeDocumentSyncError(document.sync_error_message)} — see Settings › Sync`
    : undefined;
  return <Pill text={label} color={color} size="sm" tooltip={tooltip} />;
}
